import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Pagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Search,
  MoreVert,
  Visibility,
  Edit,
  Warning,
  CheckCircle,
  Inventory,
} from "@mui/icons-material";
import {departmentStockService} from "../../services/departmentStockService";
import { useSnackbar } from "notistack";

export const DepartmentStockList = () => {
  const [departmentStocks, setDepartmentStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Filter state
  const [filters, setFilters] = useState({
    department: "",
    item: "",
    status: "",
  });

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDepartmentStocks();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    filters.department,
    filters.item,
    filters.status,
  ]);

  const fetchDepartmentStocks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.department && { department: filters.department }),
        ...(filters.item && { item: filters.item }),
        ...(filters.status && { status: filters.status }),
      };

      const response = await departmentStockService.getDepartmentStocks(params);
      const { departmentStocks: stocksData, pagination: paginationData } =
        response.data.data || response.data;

      setDepartmentStocks(stocksData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData?.total || 0,
        pages: paginationData?.pages || 0,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching department stocks", { variant: "error" });
      console.error("Error fetching department stocks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleMenuOpen = (event, stock) => {
    setAnchorEl(event.currentTarget);
    setSelectedStock(stock);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStock(null);
  };

  const handleViewDepartment = () => {
    if (selectedStock?.department?._id) {
      navigate(`/hospital/hospital-inventory/departments/${selectedStock.department._id}`);
    }
    handleMenuClose();
  };

  const handleViewItem = () => {
    if (selectedStock?.item?._id) {
      navigate(`/hospital/hospital-inventory/items/${selectedStock.item._id}`);
    }
    handleMenuClose();
  };

  const handleEditLevels = () => {
    if (selectedStock) {
      navigate(`/hospital/hospital-inventory/department-stock/${selectedStock._id}/edit`);
    }
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "success",
      depleted: "error",
      expired: "warning",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (stock) => {
    if (stock.status === "depleted") {
      return <Warning color="error" />;
    }
    if (stock.status === "expired") {
      return <Warning color="warning" />;
    }
    if (stock.quantity <= stock.minimumLevel) {
      return <Warning color="warning" />;
    }
    return <CheckCircle color="success" />;
  };

  const isLowStock = (stock) => {
    return stock.quantity <= stock.minimumLevel && stock.status === "active";
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Department Stock
        </Typography>
      </Box>

      {/* Search and Filter Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by department or item name..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="depleted">Depleted</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary" align="center">
                Total: {pagination.total} records
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Department Stocks Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Min Level</TableCell>
                  <TableCell align="right">Max Level</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Allocated Date</TableCell>
                  <TableCell>Stock Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departmentStocks.map((stock) => (
                  <TableRow
                    key={stock._id}
                    sx={{
                      "&:hover": { bgcolor: "action.hover" },
                      bgcolor: isLowStock(stock) ? "warning.light" : "inherit",
                    }}
                  >
                    <TableCell>
                      <Tooltip
                        title={
                          isLowStock(stock)
                            ? "Low Stock Warning"
                            : stock.status === "depleted"
                            ? "Depleted"
                            : stock.status === "expired"
                            ? "Expired"
                            : "Normal"
                        }
                      >
                        {getStatusIcon(stock)}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {stock.department?.departmentName || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stock.department?.departmentCode || ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {stock.item?.name || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stock.item?.brand || ""} • {stock.item?.unit || ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={isLowStock(stock) ? "warning.main" : "inherit"}
                      >
                        {stock.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {stock.minimumLevel || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {stock.maximumLevel || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {stock.batchStock?.batchNumber || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(stock.allocatedDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stock.status}
                        size="small"
                        color={getStatusColor(stock.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, stock)}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* No Results */}
          {departmentStocks.length === 0 && !loading && (
            <Card sx={{ textAlign: "center", py: 6 }}>
              <CardContent>
                <Inventory sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No department stock records found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || filters.status
                    ? "Try adjusting your search or filters"
                    : "Department stock records will appear here once items are issued to departments"}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDepartment}>
          <Visibility sx={{ mr: 1 }} />
          View Department
        </MenuItem>
        <MenuItem onClick={handleViewItem}>
          <Visibility sx={{ mr: 1 }} />
          View Item
        </MenuItem>
        <MenuItem onClick={handleEditLevels}>
          <Edit sx={{ mr: 1 }} />
          Edit Stock Levels
        </MenuItem>
      </Menu>
    </Box>
  );
};
